import React, { useState } from "react";
import styled, { css } from "styled-components";
import { Box, Flex } from "rebass/styled-components";
import Lottie from "lottie-react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { MdOutlineMovieCreation, MdBolt } from "react-icons/md";
import { HiOutlineInformationCircle } from "react-icons/hi";

import { Link } from "MVVM/Components/Button";
import { SignupToCreate } from "MVVM/Views/MatchHistory/Components/Shared/SignupToCreate";
import {
  ProcessingButton,
  TinyAvatar,
} from "MVVM/Views/MatchHistory/styledComponents";
import { useIsMobileApp } from "MVVM/Hooks/useIsMobileApp";
import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { TMoment } from "MVVM/Views/MatchHistory/@types";
import {
  IsOwnMatchHistory,
  MatchHistoryGame,
  MatchHistoryplayerToSearchFor,
  MatchHistoryWidgetSourceState,
  MatchHistoryWidgetIndentifierState,
  MomentsPlayerOverride,
} from "MVVM/State/matchHistory";
import { analytics } from "MVVM/Analytics";
import Remixing from "../../../../../../../animations/remixing.json";
import { StyledMomentIcon } from "../styledComponents";
import { MomentClip } from "../MomentClip";
import { MomentViewModel } from "./ViewModel";
import { ClipBadges } from "MVVM/Components/ClipBadge";
import { getTagBadges } from "MVVM/Utilities/ClipTags";
import { ESupportedGames } from "MVVM/@types";
import { getPlayerGameIdByGame } from "MVVM/Views/MatchHistory/utils";
import { CreatedForFriend } from "../../CreatedForFriend";
import { ProfileCardWrapper } from "MVVM/Components/ProfileCard/Components/ProfileCardWrapper";
import { QuickEdit } from "MVVM/Components/QuickEdit";
import { URLS } from "MVVM/Utilities/Maps";
import { PartnerViewSession } from "MVVM/State";
import { MatchHistoryLoginModal } from "../../LoginModal";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { useWindowSize } from "hooks";
import { ValorantComingSoonModal } from "./ValorantComingSoonModal";
import {
  QuickEditPreferredLoadout,
  QuickEditPreferredFinalTouches,
  QuickEditPreferredThumbnail,
  QuickEditPreferredPresetId,
} from "MVVM/State/quickEdit";
import { addPendingClipCreation } from "MVVM/Utilities/ClipLimits/pendingClipCreations";
import { ClipEligibilityModal } from "MVVM/Components/Modals/ClipEligibilityModal";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { TrayUIAtom } from "MVVM/State/clipStatus";
import { OutdatedMatchInterestModal } from "MVVM/Components/Modals/OutdatedMatchInterest";

const SUPPORT_URL = "https://help.allstar.gg/hc/en-us/requests/new";

interface IClipCreateError {
  message: string;
  support?: boolean;
}

const { REACT_APP_S3_CLIP_BUCKET } = process.env;
const CT_ICON = `${REACT_APP_S3_CLIP_BUCKET}/static/web/montage/ct.png`;
const T_ICON = `${REACT_APP_S3_CLIP_BUCKET}/static/web/montage/t.png`;

const Moment = ({
  moment,
  isWidget,
  outdated,
  matchId,
  sideColor,
  side,
  isBest,
}: {
  moment: TMoment;
  isWidget?: boolean;
  outdated?: boolean;
  matchId: string;
  sideColor?: string;
  side?: string;
  isBest?: boolean;
}) => {
  const { momentRequestClip, momentCreateClip } = MomentViewModel();
  const isOwnMatchHistory = useRecoilValue(IsOwnMatchHistory);
  const momentsPlayerOverride = useRecoilValue(MomentsPlayerOverride);
  const matchOwnerGameId = useRecoilValue(MatchHistoryplayerToSearchFor);
  const { allstarUser } = useUser();
  const { isMobileApp } = useIsMobileApp();
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const preferredLoadout = useRecoilValue(QuickEditPreferredLoadout);
  const preferredFinalTouches = useRecoilValue(QuickEditPreferredFinalTouches);
  const preferredThumbnail = useRecoilValue(QuickEditPreferredThumbnail);
  const preferredPresetId = useRecoilValue(QuickEditPreferredPresetId);
  const { width } = useWindowSize();
  const isOwnMoment =
    matchHistoryGame === ESupportedGames.VALORANT
      ? allstarUser?.user?.riot.riotId === moment.player.accountName
      : getPlayerGameIdByGame(matchHistoryGame, allstarUser) ===
        moment?.player?.gameId;

  const widgetSource = useRecoilValue(MatchHistoryWidgetSourceState);
  const widgetPlayerIdentifier = useRecoilValue(
    MatchHistoryWidgetIndentifierState,
  );
  const partnerViewSession = useRecoilValue(PartnerViewSession);
  const renderNewLoginModal = true;

  const useQuickEdit = matchHistoryGame !== ESupportedGames.FORTNITE;
  const {
    portalOpen: createdForFriendPortalOpen,
    setPortalOpen: setCreatedForFriendPortalOpen,
    Portal: CreatedForFriendPortal,
  } = usePortal(
    <CreatedForFriend
      game={matchHistoryGame}
      moment={moment}
      matchId={matchId}
    />,
  );
  const {
    portalOpen: quickEditPortalOpen,
    setPortalOpen: setQuickEditPortalOpen,
    Portal: QuickEditPortal,
  } = usePortal(
    <QuickEdit
      momentId={moment.id}
      closeModal={() => setQuickEditPortalOpen(false)}
      onCreate={() => {
        if (!isOwnMoment) {
          setCreatedForFriendPortalOpen(true);
        }
        setProcessing(true);
      }}
    />,
    "root",
  );
  const {
    portalOpen: createValorantComingSoonPortalOpen,
    setPortalOpen: setValorantComingSoonPortalOpen,
    Portal: ValorantComingSoonPortal,
  } = usePortal(
    <ValorantComingSoonModal
      moment={moment}
      title="Valorant Clips are Coming Soon!"
      subtitle="We're adding Match History support for Valorant in the coming weeks. Check back here often for updates!"
      closeModal={() => {
        setValorantComingSoonPortalOpen(false);
      }}
    />,
  );
  const {
    portalOpen: signupPortalOpen,
    setPortalOpen: setSignupPortalOpen,
    Portal: SignupPortal,
  } = usePortal(<SignupToCreate />);
  const {
    portalOpen: matchHistoryLoginPortalOpen,
    setPortalOpen: setMatchHistoryLoginPortalOpen,
    Portal: MatchHistoryLoginPortal,
  } = usePortal(
    <MatchHistoryLoginModal
      game={getMatchHistoryGameEnum(moment.game) || matchHistoryGame}
      moment={moment}
      title="matchHistoryLoginModal.title"
      defaultTitle="Don't Miss Out On This Play!"
      subtitle="matchHistoryLoginModal.subtitle"
      defaultSubtitle="Create a FREE Allstar.gg account to create, edit, and share a clip from this moment."
    />,
  );
  const {
    portalOpen: eligibilityModalOpen,
    setPortalOpen: setEligibilityModalOpen,
    Portal: EligibilityModalPortal,
  } = usePortal(
    <ClipEligibilityModal
      clipQuota={allstarUser?.user?.clipQuota}
      userGroup={allstarUser?.user?.userGroup?.type}
      onClose={() => setEligibilityModalOpen(false)}
    />,
  );
  const setTrayUI = useSetRecoilState(TrayUIAtom);
  const [error] = useState<IClipCreateError | null>(null);
  const timelineActive =
    moment?.timeline != null &&
    moment.timeline.overallState !== "COMPLETED" &&
    moment.timeline.overallState !== "ERRORED";
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [processing, setProcessing] = useState(
    moment?.clip?.status === "PENDING" || timelineActive,
  );

  const isPruned = moment?.clip?.status === "PRUNED";
  const isProcessed = moment?.clip?.status === "READY";
  const victimCount = moment?.victimData?.length;
  const firstTagValue = moment?.displayTags?.find(
    (tag: any) => tag.key === "tag",
  )?.value;
  const hideTags = width && width < 700;
  const filteredTags = moment.clipTags?.filter(
    (t: { key: string }) => t.key !== "CS_Round" && t.key !== "CS_Weapons",
  );
  const hasBadges =
    matchHistoryGame === ESupportedGames.CS2 &&
    moment.type === "primary" &&
    !hideTags &&
    getTagBadges(filteredTags).length > 0;

  const handleClick = async () => {
    if (widgetSource === "iframe") {
      analytics.track("Match History - Go to Match", {
        isWidget: true,
        widgetSource: widgetSource,
      });
      window.open(
        `${
          URLS.MATCH_HISTORY
        }?matchId=${matchId}&game=${matchHistoryGame}&playerSearchIdentifier=${widgetPlayerIdentifier}&partner=${partnerViewSession
          ?.clip?.partner?.partnerId
          ?.partnerId}&ajs_aid=${window.rudderanalytics.getAnonymousId()}`,
        "_blank",
      );
      return;
    }

    analytics.track("Match History - Request Clip", {
      userGroup: allstarUser?.user?.userGroup?.type,
      type: moment.type,
      tag: firstTagValue,
      isMobile: isMobileApp,
      game: matchHistoryGame,
      isWidget,
      isVisitor: !allstarUser.loggedIn,
      createForFriend: !isOwnMoment && allstarUser?.loggedIn,
      onPlatform: !!moment?.player?.user?._id,
    });

    if (!allstarUser.loggedIn) {
      if (renderNewLoginModal) return setMatchHistoryLoginPortalOpen(true);

      return setSignupPortalOpen(true);
    }

    if (!isOwnMoment) {
      analytics.track("Match History - Create For Friend", {
        type: "clip",
        game: matchHistoryGame,
        onPlatform: !!moment?.player?.user?._id,
      });
    }

    if (matchHistoryGame === ESupportedGames.VALORANT) {
      return setValorantComingSoonPortalOpen(true);
    }

    if (useQuickEdit) {
      setQuickEditPortalOpen(true);
    } else {
      setProcessing(true);
      await momentRequestClip({
        variables: {
          momentRequestClipInput: {
            id: moment.id,
          },
        },
      });
    }
  };

  const handleQuickCreate = async () => {
    if (!allstarUser.loggedIn) {
      return setMatchHistoryLoginPortalOpen(true);
    }

    analytics.track("Match History - 1-Click Clip", {
      userGroup: allstarUser?.user?.userGroup?.type,
      type: moment.type,
      game: matchHistoryGame,
      isWidget,
      createForFriend: !isOwnMoment && allstarUser?.loggedIn,
    });

    if (!isOwnMoment) {
      setCreatedForFriendPortalOpen(true);
    }

    setProcessing(true);

    const result = await momentCreateClip({
      variables: {
        momentCreateClipInput: {
          ids: [moment.id],
          matchId,
          effectsOverrides: {
            thumbnail: preferredThumbnail || undefined,
            loadout: preferredLoadout?._id || "NO_LOADOUT",
            rapid: preferredFinalTouches.includes("rapidFire"),
            contextCameras: preferredFinalTouches.includes("contextCams"),
            applySignatureMusic: preferredFinalTouches.includes("music"),
            applySignatureCards: preferredFinalTouches.includes("signature"),
            disableJumpCuts: preferredFinalTouches.includes("disableJumpCuts"),
            applyMountedCameras:
              preferredFinalTouches.includes("mountedCameras"),
            applyFinalKillCamera:
              preferredFinalTouches.includes("finalKillCamera"),
            creatorCards: [
              ...(preferredPresetId ? [preferredPresetId] : []),
              ...(preferredFinalTouches.includes("hideWatermark")
                ? ["60a6d86a8c4fd555132afc97"]
                : []),
              ...(preferredFinalTouches.includes("hideKillFeed")
                ? ["622be8b4ff38f13a9d241322"]
                : []),
            ],
          },
          checkEligibility: true,
        },
      },
    });

    const errors = result?.data?.momentCreateClip?.errors;
    const eligibilityError = errors?.find(
      (e: any) => e.field === "eligibility",
    );

    if (eligibilityError) {
      setProcessing(false);
      setEligibilityModalOpen(true);
      return;
    }

    addPendingClipCreation();
  };

  return (
    <StyledCardWrapper $isBest={isBest} $sideColor={sideColor} $side={side}>
      {createdForFriendPortalOpen && CreatedForFriendPortal}

      {signupPortalOpen && SignupPortal}
      {quickEditPortalOpen && QuickEditPortal}
      {matchHistoryLoginPortalOpen && MatchHistoryLoginPortal}
      {createValorantComingSoonPortalOpen && ValorantComingSoonPortal}
      {eligibilityModalOpen && EligibilityModalPortal}
      {showExpiredModal && (
        <OutdatedMatchInterestModal
          matchId={matchId}
          onClose={() => setShowExpiredModal(false)}
        />
      )}
      {(isProcessed || isPruned) && widgetSource !== "iframe" ? (
        <MomentClip
          moment={moment}
          outdated={outdated}
          clipTags={hasBadges ? filteredTags : undefined}
        />
      ) : (
        <Flex alignItems="center" style={{ gap: 8 }}>
          {error && (
            <ClipError>
              <StyledInfo />
              {error.message}
              {error?.support && (
                <SupportLink href={SUPPORT_URL}>Contact support?</SupportLink>
              )}
            </ClipError>
          )}
          <MomentIconGroup $sideColor={isBest ? undefined : sideColor}>
            <ProfileCardWrapper
              username={moment?.player?.user?.username}
              isMatchHistory={true}
              gameId={moment?.player?.gameId}
              displayName={moment?.player?.accountName}
              avatar={moment?.player?.user?.avatarUrl || moment?.player?.avatar}
            >
              <TinyAvatar src={moment?.player?.avatar} />
            </ProfileCardWrapper>
            {!!victimCount && (
              <>
                <StyledMomentIcon
                  src={moment.icons[0]}
                  shouldFlipImage={
                    matchHistoryGame === ESupportedGames.VALORANT
                  }
                />
                <Victims>
                  {moment?.victimData?.map((victim, index) => (
                    <ProfileCardWrapper
                      key={`enemy-${moment.id}-${victim.gameId}-${index}`}
                      username={victim.user?.username}
                      isMatchHistory={true}
                      gameId={victim.gameId}
                      displayName={victim.displayName || victim.accountName}
                      avatar={victim.user?.avatarUrl || victim.avatar}
                    >
                      <TinyAvatar src={victim.avatar} />
                    </ProfileCardWrapper>
                  ))}
                </Victims>
              </>
            )}
          </MomentIconGroup>
          {hasBadges ? (
            <InlineBadges>
              <ClipBadges tags={filteredTags} size="sm" maxVisible={2} />
            </InlineBadges>
          ) : (
            <Box flex="1" />
          )}
          <Flex flexShrink="0" style={{ gap: 8 }} alignItems="center">
            {processing ? (
              <StyledProcessingButton
                flex={true}
                size="tiny"
                onClick={() =>
                  setTrayUI((prev) => ({ ...prev, expanded: true }))
                }
              >
                Clip Pending
                <Lottie
                  animationData={Remixing}
                  loop={true}
                  style={{ height: 36, width: 36, minWidth: 36 }}
                />
              </StyledProcessingButton>
            ) : outdated ? (
              <ButtonGroup>
                <QuickCreateBtn onClick={() => setShowExpiredModal(true)}>
                  <MdBolt size={16} />
                  1-Click Clip
                </QuickCreateBtn>
                <ClipBtn onClick={() => setShowExpiredModal(true)}>
                  <StyledMdOutlineMovieCreation />
                  Edit & Clip
                </ClipBtn>
              </ButtonGroup>
            ) : (
              <ButtonGroup>
                <AnimatedTooltip
                  content={
                    <span>
                      Create a clip instantly using your{" "}
                      <QuickEditLink
                        onClick={(e) => {
                          e.preventDefault();
                          handleClick();
                        }}
                      >
                        Quick Edit preferences
                      </QuickEditLink>
                    </span>
                  }
                  maxWidth={220}
                  link={{ label: "", href: "" }}
                >
                  <QuickCreateBtn onClick={handleQuickCreate}>
                    <MdBolt size={16} />
                    1-Click Clip
                  </QuickCreateBtn>
                </AnimatedTooltip>
                <ClipBtn disabled={error} onClick={async () => handleClick()}>
                  <StyledMdOutlineMovieCreation />
                  Edit & Clip
                </ClipBtn>
              </ButtonGroup>
            )}
          </Flex>
        </Flex>
      )}
    </StyledCardWrapper>
  );
};

const InlineBadges = styled(Flex)`
  align-items: center;
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    display: none;
  }
`;

const WatermarkClip = styled(Box)`
  border-radius: 8px;
  height: 100%;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
`;

const SideWatermark = styled.img`
  height: 90px;
  left: -45px;
  opacity: 0.08;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 90px;
`;

const StyledCardWrapper = ({
  $isBest,
  $sideColor,
  $side,
  children,
}: {
  $isBest?: boolean;
  $sideColor?: string;
  $side?: string;
  children: React.ReactNode;
}) => {
  const sideIcon =
    $side === "CT" ? CT_ICON : $side === "T" ? T_ICON : undefined;
  return (
    <RegularCard $sideColor={$sideColor}>
      {sideIcon && (
        <WatermarkClip>
          <SideWatermark src={sideIcon} alt="" />
        </WatermarkClip>
      )}
      {children}
    </RegularCard>
  );
};

const RegularCard = styled(Box)<{ $sideColor?: string }>`
  background: ${({ theme }) => theme.colors.navy600};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 8px;
  flex-grow: 1;
  margin-bottom: 8px;
  min-height: 56px;
  min-width: 0;
  overflow: hidden;
  padding: 10px 12px;
  position: relative;
`;

const StyledProcessingButton = styled(ProcessingButton)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
`;

const Victims = styled(Flex)`
  flex-direction: row;
  align-items: center;

  & > * {
    margin-left: -6px;
  }
  & > *:first-child {
    margin-left: 0;
  }
`;

const StyledInfo = styled(HiOutlineInformationCircle)`
  color: ${({ theme }) => theme.colors.alert};
  height: 24px;
  width: 24px;
`;

const ClipError = styled(Flex)`
  align-items: center;
  bottom: -38px;
  color: ${({ theme }) => theme.colors.alert};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  gap: 4px;
  position: absolute;
  right: 0px;
`;

const ButtonGroup = styled(Flex)`
  gap: 6px;
`;

const buttonBase = css`
  align-items: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  gap: 4px;
  padding: 8px 12px;
  transition: all 0.15s ease;
  white-space: nowrap;
`;

const QuickCreateBtn = styled.button`
  ${buttonBase}
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  color: ${({ theme }) => theme.colors.chalk};

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const ClipBtn = styled.button<{ disabled?: any }>`
  ${buttonBase}
  background: ${({ theme }) => theme.colors.envy};
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledMdOutlineMovieCreation = styled(MdOutlineMovieCreation)`
  height: 16px;
  width: 16px;
`;

const QuickEditLink = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const SupportLink = styled(Link)`
  color: ${({ theme }) => theme.colors.alert};
  text-decoration: underline;
`;

const MomentIconGroup = styled(Flex)<{ $sideColor?: string }>`
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
`;

export { Moment };
